/** Loading facts only. The worker owns scheduling and supplies completion boundaries. */
export const LOAD_STAGES = ['assets', 'shaders', 'textures', 'preview'] as const;
export type LoadStage = (typeof LOAD_STAGES)[number];
export type LoadStageState = {
	status: 'pending' | 'active' | 'complete' | 'failed';
	transfer?: { received: number; total?: number };
	completed?: number;
	total?: number;
	detail?: 'boot' | 'recovery' | 'gpu' | 'assets';
};
export type LoadingProgress = {
	generation: number;
	sequence: number;
	status: 'loading' | 'ready' | 'failed' | 'cancelled';
	stages: Record<LoadStage, LoadStageState>;
	failure?: { stage: LoadStage; category: 'operation' | 'worker' | 'device'; retryable: boolean };
};

export function initialLoadingProgress(generation = 0): LoadingProgress {
	return {
		generation,
		sequence: 0,
		status: 'loading',
		stages: {
			assets: { status: 'active' },
			shaders: { status: 'active', detail: 'boot' },
			textures: { status: 'pending' },
			preview: { status: 'pending' }
		}
	};
}

export function isNewerLoadingProgress(next: LoadingProgress, current: LoadingProgress): boolean {
	return (
		next.generation > current.generation ||
		(next.generation === current.generation && next.sequence > current.sequence)
	);
}

export class LoadProgressTracker {
	state: LoadingProgress = initialLoadingProgress();
	constructor(private readonly publish: (state: LoadingProgress) => void) {}
	get generation(): number {
		return this.state.generation;
	}
	get active(): boolean {
		return this.state.status === 'loading';
	}
	start(recovery = false): number {
		this.state = initialLoadingProgress(this.generation + 1);
		if (recovery) this.state.stages.shaders.detail = 'recovery';
		this.emit();
		return this.generation;
	}
	update(stage: LoadStage, state: LoadStageState, generation = this.generation): void {
		if (generation !== this.generation || !this.active) return;
		const previous: LoadStageState = this.state.stages[stage];
		if (previous.status === 'complete') return;
		if (state.total !== undefined) {
			const total: number = previous.total ?? Math.max(0, state.total);
			state = {
				...state,
				total,
				completed: Math.min(total, Math.max(previous.completed ?? 0, state.completed ?? 0))
			};
		}
		this.state = { ...this.state, stages: { ...this.state.stages, [stage]: state } };
		if (LOAD_STAGES.every((key: LoadStage) => this.state.stages[key].status === 'complete')) {
			this.state = { ...this.state, status: 'ready' };
		}
		this.emit();
	}
	/** Invoke only from the existing queue-completion callback for this batch. */
	completeBatch(
		generation: number,
		nodes: number,
		total: number,
		final: boolean,
		error: unknown
	): void {
		if (generation !== this.generation || !this.active) return;
		if (error !== null && error !== undefined) {
			this.fail('textures');
			return;
		}
		this.update(
			'textures',
			{ status: final ? 'complete' : 'active', completed: nodes, total, detail: 'gpu' },
			generation
		);
		if (final) this.update('preview', { status: 'active' }, generation);
	}
	fail(
		stage: LoadStage,
		category: 'operation' | 'worker' | 'device' = 'operation',
		retryable = true
	): void {
		if (!this.active) return;
		this.state = {
			...this.state,
			status: 'failed',
			failure: { stage, category, retryable },
			stages: { ...this.state.stages, [stage]: { ...this.state.stages[stage], status: 'failed' } }
		};
		this.emit();
	}
	cancel(): void {
		this.state = { ...this.state, status: 'cancelled' };
		this.emit();
	}
	private emit(): void {
		this.state = { ...this.state, sequence: this.state.sequence + 1 };
		this.publish(this.state);
	}
}
